/**
 * Alertas (17 §3.3, D108): cartões do painel (alertas abertos, próximas sessões) e regras em Parâmetros do sistema
 * (antecedências, e-mail e destinatários; teste de envio).
 */
import { useEffect, useState } from "react";
import { useGetList, useNotify, usePermissions } from "react-admin";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/CheckCircleOutline";
import type { Permissoes } from "../lib/provedores";
import { supabase } from "../lib/supabase";
import { ListaEventos, TIPOS_EVENTO, iso, useEventos, type EventoCalendario } from "./calendario";

interface Alerta {
  id: string;
  tipo: EventoCalendario["tipo"];
  titulo: string;
  detalhe: string | null;
  link: string | null;
  data_alvo: string;
  status: "aberto" | "lido" | "resolvido";
}

/** Erro da Edge Function com a mensagem do servidor. */
async function chamarAlertas<T>(corpo: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T & { erro?: string }>("alertas", { body: corpo });
  if (error || !data) {
    let mensagem = error?.message ?? "falha";
    const contexto = (error as { context?: Response } | null)?.context;
    if (contexto) mensagem = ((await contexto.json().catch(() => null)) as { erro?: string } | null)?.erro ?? mensagem;
    throw new Error(mensagem);
  }
  return data;
}

/* ------------------------------ Painel ------------------------------ */

export function AlertasAbertos() {
  const navigate = useNavigate();
  const notify = useNotify();
  const cliente = useQueryClient();
  const [atualizando, setAtualizando] = useState(false);
  const { data: alertas = [], error, isPending } = useQuery({
    queryKey: ["alertas_abertos"],
    queryFn: async () => {
      const { data, error: e } = await supabase.from("alerta").select("id, tipo, titulo, detalhe, link, data_alvo, status").neq("status", "resolvido").order("data_alvo");
      if (e) throw new Error(e.message);
      return (data ?? []) as Alerta[];
    },
  });

  async function marcar(id: string, status: "lido" | "resolvido") {
    const { error: e } = await supabase.rpc("marcar_alerta", { p_id: id, p_status: status });
    if (e) notify(`Não foi possível marcar: ${e.message}`, { type: "error" });
    void cliente.invalidateQueries({ queryKey: ["alertas_abertos"] });
  }

  async function atualizar() {
    setAtualizando(true);
    const { error: e } = await supabase.rpc("gerar_alertas");
    setAtualizando(false);
    if (e) notify(`Não foi possível atualizar: ${e.message}`, { type: "error" });
    void cliente.invalidateQueries({ queryKey: ["alertas_abertos"] });
  }

  const hoje = iso(new Date());
  return (
    <Card>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Alertas {alertas.length ? `(${alertas.length})` : ""}</Typography>
          <Button size="small" onClick={() => void atualizar()} disabled={atualizando}>
            {atualizando ? "Atualizando…" : "Atualizar"}
          </Button>
        </Stack>
        {error && <Alert severity="error">{error.message}</Alert>}
        {!error && alertas.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {isPending ? "Carregando…" : "Nenhum alerta em aberto."}
          </Typography>
        )}
        <Stack spacing={0.75}>
          {alertas.map((a) => {
            const t = TIPOS_EVENTO[a.tipo];
            return (
              <Stack key={a.id} direction="row" spacing={1} alignItems="flex-start" sx={{ opacity: a.status === "lido" ? 0.6 : 1 }}>
                <Box sx={{ width: 4, alignSelf: "stretch", bgcolor: t.cor, borderRadius: 1, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography variant="body2" fontWeight={a.status === "aberto" ? 700 : 400}>
                    {a.data_alvo === hoje && <Chip size="small" label="Hoje" color="secondary" sx={{ mr: 0.5, height: 18 }} />}
                    {a.link ? (
                      <Link component="button" variant="body2" onClick={() => navigate(a.link ?? "/")} sx={{ textAlign: "left", fontWeight: "inherit" }}>
                        {a.titulo}
                      </Link>
                    ) : (
                      a.titulo
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t.nome} · {a.detalhe}
                  </Typography>
                </Box>
                {a.status === "aberto" && (
                  <Tooltip title="Marcar como lido">
                    <Button size="small" onClick={() => void marcar(a.id, "lido")} sx={{ minWidth: 0 }}>
                      Lido
                    </Button>
                  </Tooltip>
                )}
                <Tooltip title="Resolvido: sai da lista">
                  <Button size="small" onClick={() => void marcar(a.id, "resolvido")} sx={{ minWidth: 0 }} aria-label="Resolvido">
                    <CheckIcon fontSize="small" />
                  </Button>
                </Tooltip>
              </Stack>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}

export function ProximasSessoes() {
  const navigate = useNavigate();
  const hoje = new Date();
  const fim = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() + 7);
  const { data: eventos = [], isPending, error } = useEventos(iso(hoje), iso(fim));
  const sessoes = eventos.filter((e) => e.tipo === "sessao" && !["descartada", "suspensa"].includes(e.situacao ?? ""));
  return (
    <Card>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Próximas sessões (7 dias)</Typography>
          <Button size="small" onClick={() => navigate("/calendario")}>
            Calendário
          </Button>
        </Stack>
        {error && <Alert severity="error">{error.message}</Alert>}
        <ListaEventos eventos={sessoes} vazio={isPending ? "Carregando…" : "Nenhuma sessão nos próximos 7 dias."} />
      </CardContent>
    </Card>
  );
}

/* ------------------------------ Parâmetros ------------------------------ */

interface Regra {
  tipo: EventoCalendario["tipo"];
  nome: string;
  antecedencias: number[];
  ativo: boolean;
  enviar_email: boolean;
  destinatarios: string[];
}

interface Usuario {
  id: string;
  nome: string;
  email: string;
  ativo: boolean;
}

const textoDias = (d: number[]) => d.join(", ");
const diasDoTexto = (t: string): number[] | null => {
  const partes = t
    .split(/[,;\s]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (partes.length === 0 || partes.length > 5) return null;
  const n = partes.map(Number);
  return n.every((x) => Number.isInteger(x) && x >= 0 && x <= 365) ? [...new Set(n)].sort((a, b) => b - a) : null;
};

function LinhaRegra({ regra, usuarios, admin, aoGravar }: { regra: Regra; usuarios: Usuario[]; admin: boolean; aoGravar: () => void }) {
  const notify = useNotify();
  const [dias, setDias] = useState(textoDias(regra.antecedencias));
  useEffect(() => setDias(textoDias(regra.antecedencias)), [regra.antecedencias]);
  const diasValidos = diasDoTexto(dias);

  async function gravar(patch: Partial<Regra>) {
    const { error } = await supabase.from("regra_alerta").update(patch).eq("tipo", regra.tipo);
    if (error) notify(`Não foi possível gravar: ${error.message}`, { type: "error" });
    aoGravar();
  }

  const selecionados = usuarios.filter((u) => regra.destinatarios.includes(u.id));
  return (
    <Grid container spacing={1} alignItems="center" sx={{ py: 1, borderBottom: 1, borderColor: "divider" }}>
      <Grid size={{ xs: 12, md: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: TIPOS_EVENTO[regra.tipo].cor }} />
          <Typography variant="body2" fontWeight={700}>
            {regra.nome}
          </Typography>
        </Stack>
      </Grid>
      <Grid size={{ xs: 6, md: 2 }}>
        <TextField
          size="small"
          label={regra.tipo === "cotacao_vencida" ? "Dias após" : "Dias antes"}
          value={dias}
          onChange={(e) => setDias(e.target.value)}
          onBlur={() => diasValidos && textoDias(diasValidos) !== textoDias(regra.antecedencias) && void gravar({ antecedencias: diasValidos })}
          error={!diasValidos}
          helperText={!diasValidos ? "Ex.: 15, 3" : regra.tipo === "cotacao_vencida" ? "0 = no dia em que vence" : ""}
          disabled={!admin}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 6, md: 2 }}>
        <Stack>
          <FormControlLabel control={<Switch size="small" checked={regra.ativo} disabled={!admin} onChange={(e) => void gravar({ ativo: e.target.checked })} />} label="Ativo" />
          <FormControlLabel
            control={<Switch size="small" checked={regra.enviar_email} disabled={!admin || !regra.ativo} onChange={(e) => void gravar({ enviar_email: e.target.checked })} />}
            label="E-mail"
          />
        </Stack>
      </Grid>
      <Grid size={{ xs: 12, md: 5 }}>
        <Autocomplete
          multiple
          size="small"
          options={usuarios.filter((u) => u.ativo)}
          getOptionLabel={(u) => u.nome}
          value={selecionados}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={(_, v) => void gravar({ destinatarios: v.map((u) => u.id) })}
          disabled={!admin || !regra.enviar_email}
          renderInput={(p) => <TextField {...p} label="Destinatários do e-mail" />}
        />
      </Grid>
    </Grid>
  );
}

interface Diagnostico {
  host: string;
  porta: string | null;
  usuario: string;
  usuario_com_espaco: boolean;
  remetente: string | null;
  senha_tamanho: number;
  senha_com_espaco_nas_pontas: boolean;
  senha_tem_aspas: boolean;
  testes: { nome: string; ok: boolean; erro?: string }[];
}

export function RegrasAlertaParametro() {
  const notify = useNotify();
  const { permissions } = usePermissions<Permissoes | null>();
  const admin = permissions?.papel === "admin";
  const { data: regras = [], refetch } = useQuery({
    queryKey: ["regra_alerta"],
    queryFn: async () => {
      const { data, error } = await supabase.from("regra_alerta").select("tipo, nome, antecedencias, ativo, enviar_email, destinatarios");
      if (error) throw new Error(error.message);
      return (data ?? []) as Regra[];
    },
  });
  const { data: usuarios = [] } = useGetList<Usuario>("usuario", { pagination: { page: 1, perPage: 200 }, sort: { field: "nome", order: "ASC" } });
  const [situacao, setSituacao] = useState<{ configurado: boolean; remetente: string | null } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [diagnostico, setDiagnostico] = useState<Diagnostico | null>(null);

  useEffect(() => {
    chamarAlertas<{ configurado: boolean; remetente: string | null }>({ acao: "situacao" })
      .then(setSituacao)
      .catch(() => setSituacao(null));
  }, []);

  async function diagnosticar() {
    setOcupado(true);
    try {
      setDiagnostico(await chamarAlertas<Diagnostico>({ acao: "diagnostico" }));
    } catch (e) {
      notify(`Não foi possível fazer o diagnóstico: ${(e as Error).message}`, { type: "error" });
    }
    setOcupado(false);
  }

  async function executar(acao: "teste" | "diario") {
    setOcupado(true);
    try {
      const r = await chamarAlertas<{ para?: string; enviados?: number; aviso?: string; falhas?: string[] }>({ acao });
      if (acao === "teste") notify(`E-mail de teste enviado para ${r.para}.`, { type: "success" });
      else notify(r.aviso ?? `Resumo enviado para ${r.enviados} pessoa(s).${r.falhas?.length ? ` Falhas: ${r.falhas.join("; ")}` : ""}`, { type: r.falhas?.length ? "warning" : "success" });
    } catch (e) {
      notify(`Não foi possível enviar: ${(e as Error).message}`, { type: "error" });
    }
    setOcupado(false);
  }

  const ordem: EventoCalendario["tipo"][] = ["sessao", "impugnacao", "certidao", "assinatura_portal", "cotacao_vencida"];
  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontSize: "1rem" }}>
          Alertas
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Os alertas aparecem no painel e saem por e-mail num resumo diário às 7h. Cada data gera um alerta a partir da maior antecedência; o de menor
          antecedência substitui o anterior. Somem sozinhos quando a data passa ou a situação muda.
        </Typography>
        {situacao && !situacao.configurado && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            E-mail não configurado: crie os segredos SMTP_HOST, SMTP_PORT, SMTP_USER e SMTP_PASS nas Edge Functions do Supabase. Os alertas continuam no painel.
          </Alert>
        )}
        {situacao?.configurado && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            Remetente: <strong>{situacao.remetente}</strong>
          </Typography>
        )}
        {ordem
          .map((t) => regras.find((r) => r.tipo === t))
          .filter((r): r is Regra => Boolean(r))
          .map((r) => (
            <LinhaRegra key={r.tipo} regra={r} usuarios={usuarios} admin={admin} aoGravar={() => void refetch()} />
          ))}
        {admin && (
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={() => void executar("teste")} disabled={ocupado || !situacao?.configurado}>
              Enviar e-mail de teste
            </Button>
            <Button variant="outlined" onClick={() => void executar("diario")} disabled={ocupado || !situacao?.configurado}>
              Enviar resumo agora
            </Button>
            <Button onClick={() => void diagnosticar()} disabled={ocupado || !situacao?.configurado}>
              Diagnóstico do e-mail
            </Button>
          </Stack>
        )}
        {diagnostico && (
          <Alert severity={diagnostico.testes.some((t) => t.ok) ? "success" : "error"} sx={{ mt: 2 }} onClose={() => setDiagnostico(null)}>
            <Typography variant="body2">
              Servidor <strong>{diagnostico.host}</strong>, porta {diagnostico.porta ?? "—"} · usuário <strong>{diagnostico.usuario || "(vazio)"}</strong>
              {diagnostico.usuario_com_espaco ? " (com espaço sobrando)" : ""} · remetente {diagnostico.remetente ?? "—"}
            </Typography>
            <Typography variant="body2">
              Senha recebida: {diagnostico.senha_tamanho} caracteres
              {diagnostico.senha_com_espaco_nas_pontas ? " · COM ESPAÇO no começo ou no fim" : ""}
              {diagnostico.senha_tem_aspas ? " · COM ASPAS no começo ou no fim" : ""}
            </Typography>
            {diagnostico.testes.map((t) => (
              <Typography key={t.nome} variant="body2">
                {t.ok ? "✔" : "✖"} {t.nome}
                {t.erro ? `: ${t.erro}` : ": login aceito"}
              </Typography>
            ))}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
